/**
 * Research Modes / Blueprints System
 *
 * Provides persona-specific research configurations for different user types:
 * - Financial Analyst (Marcus): Bull vs Bear debate, Kill Sheet, CSV export
 * - Strategic Consultant (Elena): SWOT analysis, Market Fit vs Risks
 */

// Research mode identifiers
export type ResearchMode = 'auto' | 'general' | 'financial' | 'strategic';

// Agent role definitions for each mode
export interface AgentRole {
  id: string;
  name: string;           // Display name (e.g., "Risk Analyst")
  description: string;    // Role description
  perspective: string;    // The perspective this agent takes
  icon: string;           // Lucide icon name
  color: string;          // Tailwind color class
}

// Rejection reason classifications for Kill Sheet
export type RejectionReason = 
  | 'REJECTED_OUTDATED'      // Cited older data when newer exists
  | 'REJECTED_SOURCE_TIER'   // Lower quality source vs higher quality
  | 'REJECTED_CONSENSUS'     // Minority opinion vs majority
  | 'REJECTED_MAGNITUDE'     // Risk exists but is immaterial
  | 'REJECTED_PRICED_IN'     // Market already reflects this concern
  | 'REJECTED_SPECULATIVE'   // Relies on unverifiable assumptions
  | 'REJECTED_CONTRADICTED'; // Directly contradicted by evidence

// Rejected argument structure for Kill Sheet
export interface RejectedArgument {
  id: string;
  argumentSummary: string;      // Brief summary of the rejected argument
  arguedBy: string;             // Agent role that made the argument
  agentModel: string;           // Underlying model (e.g., "Claude 3.5")
  rejectionReason: RejectionReason;
  rejectionDetail: string;      // Detailed explanation of why it was rejected
  counterEvidence?: string;     // The evidence that countered this argument
  sourcesCited?: string[];      // Sources that contradicted this argument
  taskIndex: number;            // Which task this relates to
}

// Data source with timestamp for radical transparency
export interface DataSourceTimestamp {
  source: string;           // Source name (e.g., "NYSE", "Finnhub", "SEC EDGAR")
  dataType: string;         // Type of data (e.g., "Stock Price", "Financial Statement")
  timestamp: string;        // ISO timestamp of when data was fetched
  delay?: string;           // Known delay (e.g., "15min", "Real-time", "EOD")
  isRealtime: boolean;      // Whether this is real-time data
}

// Export format options
export type ExportFormat = 'pdf' | 'csv' | 'json' | 'markdown';

// Mode-specific configuration
export interface ResearchModeConfig {
  id: ResearchMode;
  name: string;
  shortName: string;        // For pill selector
  description: string;
  icon: string;             // Lucide icon name
  color: string;            // Primary color for the mode
  // Agent configuration
  primaryAgents: AgentRole[];
  // Debate configuration
  debateFocus: string;      // What the debate centers on
  debateFraming: {
    sideA: string;          // e.g., "Bull Case"
    sideB: string;          // e.g., "Bear Case"
  };
  // Data sources prioritized for this mode
  priorityDataSources: string[];
  // Output configuration
  verdictOutputType: string;  // e.g., "Kill Sheet", "Executive Summary"
  exportFormats: ExportFormat[];
  // UI customization
  showKillSheet: boolean;     // Whether to show rejected arguments
  showDataTimestamps: boolean; // Whether to show data freshness
  // Tags for categorization (e.g., 'alpha', 'beta', 'deprecated')
  // Alpha-tagged modes are excluded from auto-detection and require manual selection
  tags?: string[];
}

// Rejection reason display configuration
export const REJECTION_REASON_CONFIG: Record<RejectionReason, {
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
  color: string;
}> = {
  REJECTED_OUTDATED: {
    label: 'Outdated Data',
    shortLabel: 'Outdated',
    description: 'Argument cited older data when more recent data was available',
    icon: 'clock',
    color: 'text-amber-400',
  },
  REJECTED_SOURCE_TIER: {
    label: 'Lower Quality Source',
    shortLabel: 'Source Quality',
    description: 'Argument relied on less authoritative sources',
    icon: 'file-warning',
    color: 'text-orange-400',
  },
  REJECTED_CONSENSUS: {
    label: 'Minority Opinion',
    shortLabel: 'Minority',
    description: 'Argument was not supported by the majority of agents',
    icon: 'users',
    color: 'text-blue-400',
  },
  REJECTED_MAGNITUDE: {
    label: 'Immaterial Risk',
    shortLabel: 'Immaterial',
    description: 'Risk exists but is too small to be significant',
    icon: 'minimize-2',
    color: 'text-text-secondary',
  },
  REJECTED_PRICED_IN: {
    label: 'Already Priced In',
    shortLabel: 'Priced In',
    description: 'Market has already accounted for this concern',
    icon: 'trending-up',
    color: 'text-green-400',
  },
  REJECTED_SPECULATIVE: {
    label: 'Speculative',
    shortLabel: 'Speculative',
    description: 'Argument relies on unverifiable assumptions',
    icon: 'help-circle',
    color: 'text-purple-400',
  },
  REJECTED_CONTRADICTED: {
    label: 'Contradicted by Evidence',
    shortLabel: 'Contradicted',
    description: 'Argument was directly contradicted by available evidence',
    icon: 'x-circle',
    color: 'text-red-400',
  },
};

